import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import { useDataGroupLoadQuery, useDataGroupUpdateMutation } from "../../services/server/DataGroupApi";
import type { DataGroupEntity } from "../../types/DataGroupEntity";

interface DataGroupFormProps extends FormProps {
    onAccessErrorChange?: (hasError: boolean) => void;
}

export const DataGroupEditForm: FC<DataGroupFormProps> = ({ runSubmit, onAccessErrorChange }) => {
    const navigate = useNavigate();
    const { id } = useParams();

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("data-group");

    const [formData, setFormData] = useState<DataGroupEntity>({} as DataGroupEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress, error: accessError } = useDataGroupLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
            onAccessErrorChange?.(false);
        } else if (accessError) {
            onAccessErrorChange?.(true);
        }
    }, [loadFormData, accessError, onAccessErrorChange]);

    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useDataGroupUpdateMutation();
    const dispatch = useDispatch();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });
            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccess", {
                            id: result.data.id,
                            resource: "DataGroup",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.DATA_GROUPS}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || loadFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<DataGroupEntity>
                    name="DataGroupEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
